<template>
	<div id="app">
		<!-- NAVBAR -->
		<b-navbar type="dark" variant="dark" sticky>
			<b-navbar-brand href="#" variant="light">
				Deduplicator
				<span v-if="uploadedFileName" class="ml-2">
					<span style="color: #8899ab !important;">( Uploaded file: </span>
					<span style="font-weight: 600;color: rgb(169 190 213) !important;">{{ uploadedFileName }}</span>
					<span style="color: #8899ab !important;"> )</span>
				</span>
			</b-navbar-brand>
			<b-navbar-nav class="ml-auto align-items-center">
				<div class="mr-2">{{ saveStatus }}</div>
				<b-button v-if="saveStatus !== 'Saved'" class="mr-2 btn btn-success" @click="save">Save</b-button>
				<b-button v-if="dupes" class="btn btn-danger" @click="close">Clear</b-button>

				<!-- Cite Button -->
				<b-button id="tooltip-cite" style="border-radius: 20px; width: 90px; margin-left: 6px; background-color: #6c757d; border-color: #6c757d;" class="btn btn-secondary" @click="openPreprint">Cite</b-button>
				<b-tooltip style="width:400px !important;" target="tooltip-cite" triggers="hover" placement="bottom">
					<h6>Click here to view the publication</h6>
				</b-tooltip>

				<!-- Help Button -->
				<b-button id="tooltip-help" style="border-radius: 20px; width: 90px; margin-left: 6px; background-color: #21a5d5;" class="btn btn-info" @click="openHelp">Help</b-button>
				<b-tooltip style="width:400px !important;" target="tooltip-help" triggers="hover" placement="bottom">
					<h6>Click here to see help page</h6>
				</b-tooltip>
			</b-navbar-nav>
		</b-navbar>

		<!-- Toolbar -->
		<div class="sticky-toolbar">
			<TheToolbar />
		</div>

		<!-- Spacer to push down content (to prevent content from going underneath title/toolbar) -->
		<div class="spacer">
			&nbsp;
		</div>

		<!-- FILEUPLOAD -->
		<template v-if="!dupes">
			<b-container class="file-container">
				<FileUpload />
				<!-- <b-button class="mt-3" variant="info" @click="loadDemo">Click here to use demo library</b-button> -->
			</b-container>
		</template>

		<!-- LOADING Progress -->
		<div v-if="isLoading" class="m-3 p-2 border border-secondary rounded">
			Import Progress:
			<b-progress :value="progress.parsing" :max="1" show-progress animated></b-progress>
			<div v-if="!(progress.parsingScreened===null)">
				Screened Import Progress:
				<b-progress
					:value="progress.parsingScreened"
					:max="1"
					show-progress animated
				></b-progress>
			</div>
			Deduplication Progress:
			<b-progress :value="progress.deduping" :max="1" show-progress animated></b-progress>
			Total:
			<b-progress v-if="!(progress.parsingScreened===null)" :max="1" show-progress>
				<b-progress-bar :value="progress.parsing * 0.4" variant="success"></b-progress-bar>
				<b-progress-bar :value="progress.parsingScreened * 0.4" variant="info"></b-progress-bar>
				<b-progress-bar :value="progress.deduping * 0.2" variant="warning"></b-progress-bar>
			</b-progress>
			<b-progress v-else :max="1" show-progress>
				<b-progress-bar :value="progress.parsing * 0.8" variant="success"></b-progress-bar>
				<b-progress-bar :value="progress.deduping * 0.2" variant="warning"></b-progress-bar>
			</b-progress>
		</div>

		<!-- TABLE -->
		<template v-if="dupes">
			<DupesArea />
		</template>

		<!-- Fixed footer which is used to display extra reference information -->
		<div v-if="showEditReference" id="fixed-footer" class="resizable">
			<div id="handle"></div>
			<EditReference :id="editReference" />
		</div>

		<!-- Warning Modal -->
		<WarningModal />

		<!-- Citation Modal Component -->
		<CitationModal v-model="isCitationModalOpen" />
	</div>
</template>

<script>
import { mapGetters } from "vuex";

import FileUpload from './components/FileUpload.vue'
import WarningModal from './components/WarningModal.vue'
import DupesArea from './components/DupesArea.vue'
import TheToolbar from './components/TheToolbar.vue'
import EditReference from './components/EditReference.vue';
import CitationModal from './components/CitationModal.vue';

export default {
	name: 'App',
	components: {
		FileUpload,
		WarningModal,
		DupesArea,
		TheToolbar,
		EditReference,
		CitationModal
	},
	computed: {
		...mapGetters('duplicates', {
			dupes: 'getDupes'
		}),
		...mapGetters('metadata', {
			title: 'getTitle',
			uploadedFileName: 'getUploadedFileName'
		}),
		...mapGetters('__tera_file_sync', {
      saveStatus: 'getSaveStatus'
    })
	},
	data() {
		return {
			isLoading: false,
			isEditTitle: false,
			showEditReference: false,
			editReference: null,
			isCitationModalOpen: false,
			progress: {
				parsing: 0,
				parsingScreened: null,
				deduping: 0
			}
		}
	},
	mounted() {
		// Set loading status
		this.$root.$on('set-loading', isLoading => this.isLoading = isLoading);
		// Set progress
		this.$root.$on('set-progress', progressObj => {
			this.progress[progressObj.stage] = progressObj.progress
		});
		// Show edit footer
		this.$root.$on('show-edit', id => {
			this.editReference = id;
			this.showEditReference = true;
			// Resizable bottom fixed div
			this.$nextTick(function () {
				var handle = document.querySelector("#handle");
				var div = document.querySelector(".resizable");
				var divElm;
				var startOffset;

				handle.style.cursor = 'row-resize';
				handle.addEventListener('mousedown', function(e) {
					e.preventDefault();
					divElm = div;
					startOffset = div.offsetHeight - (window.innerHeight - e.pageY);
				})

				document.addEventListener('mousemove', function (e) {
					if (divElm) {
						if (startOffset + (window.innerHeight - e.pageY) < 200) {
							divElm.style.height = "200px";
						} else if (startOffset + (window.innerHeight - e.pageY) > (window.innerHeight * 0.9)) {
							divElm.style.height = window.innerHeight * 0.9 + 'px';
						} else {
							divElm.style.height = startOffset + (window.innerHeight - e.pageY) + 'px';
						}
					}
				}, { passive: true });

				document.addEventListener('mouseup', function () {
					divElm = undefined;
				});
			});
		});
		// Hide edit footer
		this.$root.$on('hide-edit', () => {
			this.showEditReference = false;
			this.editReference = null;
		});
	},
	methods: {
		close() {
			// this.$root.$emit('show-warning-modal');
			this.$store.dispatch('resetStore');
			this.$store.commit('metadata/setUploadedFileName', '')
		},
		save() {
			this.$teraSync.saveState();
		},
		openHelp() {
			window.open("https://tera-tools.com/help/deduplicator", "_blank");
		},
		openPreprint() {
			window.open("https://pubmed.ncbi.nlm.nih.gov/39095913/", "_blank");
		},
		saveTitle() {
			this.$store.commit('metadata/setTitle', this.$refs['title-edit'].value);
			this.isEditTitle = false;
		},
	}
}
</script>

<style lang="scss">
@import 'theme.scss';

#app {
	min-height: 100vh;
	background-color: $gray-300;
}

.table-area {
	height: calc(100vh - 100px);
}

.sticky-toolbar {
	position: fixed;
	z-index: 104;
	width: 100%;
}

/* Footer css */
#fixed-footer {
	bottom: 0px !important;
	height: 50vh;
	overflow-y: scroll;
	position: fixed;
	z-index: 100;
	width: 100%;
}

/* Footer handle css */
#handle {
	position: sticky;
	top: 0px;
	left: 0px;
	width: calc(100% - 70px);
	height: 40px;
	z-index: 102;
	margin-bottom: -40px;
}

.navbar {
	background-image: linear-gradient(#6880b5, #465b8b, #5971a5);
	color: #dcdee5;
}
</style>